import {
  APPROVAL_WORK_FLOW_RIGHTS,
  BASELINE_DATE_RIGHTS,
  GENERAL_RIGHTS,
  INITIATIVE_RIGHTS,
  NOTIFICATION_GROUP_RIGHTS,
  PERMISSION_RIGHTS,
  PROJECT_RIGHTS,
  TEMPLATE_RIGHTS,
  USER_RIGHTS,
} from "@/enums/Rights";
import { Collapse, Space, Spin, Switch } from "antd";
import { arrayOf, bool, func, string } from "prop-types";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { toggleRights } from "../helper/toggleRights";
import { PermissionRight } from "./PermissionRight";

export const PermissionRights = ({
  selectedRights = [],
  setSelectedRights,
  canEditRights,
}) => {
  const intl = useIntl();

  const rights = useMemo(() => {
    const buildRights = (rights, category) => ({
      category,
      rights: Object.keys(rights).map((right) => ({
        id: rights[right],
        description: intl.formatMessage({ id: `permissions.${right}` }),
      })),
    });

    return [
      buildRights(APPROVAL_WORK_FLOW_RIGHTS, "Approval Work Flow"),
      buildRights(GENERAL_RIGHTS, "General"),
      buildRights(PERMISSION_RIGHTS, "Permission"),
      buildRights(PROJECT_RIGHTS, "Project"),
      buildRights(TEMPLATE_RIGHTS, "Template"),
      buildRights(USER_RIGHTS, "User"),
      buildRights(
        NOTIFICATION_GROUP_RIGHTS,
        // TODO: User Story 34420: Update Distribution Group Naming
        // https://dev.azure.com/MilwaukeeToolAppDev/Moon%20Men/_workitems/edit/34420
        "Distribution Group"
      ),
      buildRights(BASELINE_DATE_RIGHTS, "Baseline Date"),
      buildRights(INITIATIVE_RIGHTS, "Initiative"),
    ];
  }, [intl.locale]);

  const permissionRights = useMemo(
    () =>
      rights.map(({ category, rights }) => {
        const selectedCount = rights.filter(({ id }) =>
          selectedRights.includes(id)
        ).length;
        return {
          category: category,
          allSelected: selectedCount === rights.length,
          selectedCount: selectedCount,
          allCount: rights.length,
          disabled: !canEditRights,
          rights: rights.map(({ id, description }) => ({
            id: id,
            description: description,
            selected: selectedRights.includes(id),
            disabled: !canEditRights,
          })),
        };
      }),
    [canEditRights, rights, selectedRights]
  );

  const handleTogglePermission = (selected, permissionRights) => {
    const toggledRights = permissionRights.map(({ id }) => id);

    setSelectedRights(toggleRights(selected, toggledRights, selectedRights));
  };

  const handleToggleRight = ({ id }, selected) => {
    setSelectedRights(toggleRights(selected, [id], selectedRights));
  };

  if (!permissionRights.length) return <Spin />;

  const items = permissionRights.map(
    ({ category, selectedCount, allCount, id, allSelected, rights, disabled }) => ({
      key: category,
      label: category,
      extra: (
        <Space direction={"horizontal"}>
          {`${selectedCount}/${allCount}`}
          <Switch
            data-test-id={`permission-selection-generate-permission-${id}`}
            checked={allSelected}
            onChange={(checked) => handleTogglePermission(checked, rights)}
            disabled={disabled}
            onClick={(s, e) => {
              if (e?.stopPropagation) e.stopPropagation();
              if (s?.stopPropagation) s.stopPropagation();
            }}
          />
        </Space>
      ),
      children: rights.map((right) => (
        <PermissionRight
          key={`${category}-${right.id}`}
          right={right}
          handleToggleRight={handleToggleRight}
        />
      )),
    })
  );

  return <Collapse items={items} />;
};

PermissionRights.propTypes = {
  selectedRights: arrayOf(string),
  setSelectedRights: func.isRequired,
  canEditRights: bool,
};
